import { cva, type VariantProps } from 'class-variance-authority';
import type * as React from 'react';

import { cn } from '~utils/cn';

/**
 * Wrapper "box" styles. The visible field (border, background, height, focus
 * ring) lives on a flex container so `prefix`/`suffix` slots can sit alongside
 * the input. Disabled and invalid states are mirrored from the inner input via
 * `has-*` selectors. The size ladder mirrors the Button size ladder.
 */
const inputVariants = cva(
  'flex w-full min-w-0 items-center gap-2 rounded-md border border-input bg-background text-base shadow-xs outline-none transition-[color,box-shadow] focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/50 has-[input:disabled]:cursor-not-allowed has-[input:disabled]:opacity-50 has-[input[aria-invalid=true]]:border-destructive has-[input[aria-invalid=true]]:ring-destructive/20 md:text-sm dark:bg-input/30 dark:has-[input[aria-invalid=true]]:ring-destructive/40',
  {
    variants: {
      size: {
        xs: 'h-6 gap-1.5 px-2 text-xs',
        sm: 'h-8 px-3',
        default: 'h-10 px-3',
        lg: 'h-12 px-4',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  },
);

type InputProps = Omit<React.ComponentProps<'input'>, 'prefix' | 'size'> &
  VariantProps<typeof inputVariants> & {
    /** Content rendered before the field (e.g. a leading search icon). */
    prefix?: React.ReactNode;
    /** Content rendered after the field (e.g. a trailing clear button). */
    suffix?: React.ReactNode;
  };

const slotClassName = "flex shrink-0 items-center text-muted-foreground [&_svg:not([class*='size-'])]:size-5";

function Input({ className, type, size, prefix, suffix, ...props }: InputProps) {
  return (
    <div data-slot="input-wrapper" data-size={size ?? 'default'} className={cn(inputVariants({ size }), className)}>
      {prefix ? (
        <span data-slot="input-prefix" className={slotClassName}>
          {prefix}
        </span>
      ) : null}
      <input
        type={type}
        data-slot="input"
        className="h-full min-w-0 grow bg-transparent text-foreground outline-none selection:bg-primary selection:text-primary-foreground file:inline-flex file:h-7 file:border-0 file:bg-transparent file:font-medium file:text-foreground file:text-sm placeholder:text-muted-foreground disabled:cursor-not-allowed"
        {...props}
      />
      {suffix ? (
        <span data-slot="input-suffix" className={slotClassName}>
          {suffix}
        </span>
      ) : null}
    </div>
  );
}

export { Input, inputVariants };
